import { useEffect, useState, useCallback } from "react";
import { PageLayout } from "@/ui/layouts";
import { Button, Card, CardContent, Loader, MonthYearPicker } from "@/ui/atoms";
import { FileUploadZone } from "@/ui/organisms";
import { FormField } from "@/ui/molecules";
import { getCurrentPeriod } from "@/utils/utils";
import { Upload, CheckCircle2, ShieldX, AlertCircle, RefreshCw } from "lucide-react";

type TDealOption = {
    id: string;
    name: string;
};

type TFinancialFileOption = {
    id: string;
    name: string;
};

type TFileUploadStatus = "pending" | "uploading" | "success" | "error";

type TFileUploadEntry = {
    file: File;
    status: TFileUploadStatus;
    error?: string;
};

/**
 * View parameters (injected dependencies)
 */
export type TViewUploadSupportingParams = {
    useCases: () => {
        onMount: () => void;
        onUnmount: () => void;
        useDeals: () => { data: TDealOption[] | null; loading: boolean };
        useFinancialFiles: (dealId: string | null) => {
            data: TFinancialFileOption[] | null;
            loading: boolean;
        };
        canUpload: boolean;
        onUpload: (data: {
            dealId: string;
            friendlyName: string;
            /** "YYYY-MM" */
            period: string;
            financialFileId: string | null;
            notes: string | null;
            file: File;
        }) => Promise<void>;
    };
};

/**
 * View props (from router/parent)
 */
export type TViewUploadSupportingProps = Record<string, never>;

/** Accepted file types for supporting files */
const supportingFileAccept: Record<string, string[]> = {
    "application/pdf": [".pdf"],
    "application/msword": [".doc"],
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [".docx"],
    "application/vnd.ms-excel": [".xls"],
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [".xlsx"],
    "application/vnd.ms-powerpoint": [".ppt"],
    "application/vnd.openxmlformats-officedocument.presentationml.presentation": [".pptx"],
    "image/jpeg": [".jpg", ".jpeg"],
    "image/png": [".png"],
    "image/gif": [".gif"],
    "image/bmp": [".bmp"],
    "image/tiff": [".tiff"],
    "text/plain": [".txt"],
    "text/csv": [".csv"],
};

const MAX_FILES = 25;
const CONCURRENCY_LIMIT = 3;

function stripExtension(filename: string): string {
    const lastDot = filename.lastIndexOf(".");
    return lastDot > 0 ? filename.substring(0, lastDot) : filename;
}

/**
 * Creates the Upload Supporting Files view component.
 *
 * The Business Partner's standalone entry point for supporting files (sidebar
 * Upload → Supporting Files): pick a deal and period, then upload a batch.
 * Renaming, deleting and listing stay on the review page's Supporting Files tab.
 */
export const createViewUploadSupporting = (params: TViewUploadSupportingParams) => {
    const ViewUploadSupporting: React.FC<TViewUploadSupportingProps> = () => {
        const { onMount, onUnmount, useDeals, useFinancialFiles, canUpload, onUpload } =
            params.useCases();

        const dealsState = useDeals();
        const [selectedDealId, setSelectedDealId] = useState("");
        const [period, setPeriod] = useState(getCurrentPeriod());
        const [financialFileId, setFinancialFileId] = useState("");
        const [notes, setNotes] = useState("");
        const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
        const [isSubmitting, setIsSubmitting] = useState(false);

        // Batch upload progress tracking
        const [batchEntries, setBatchEntries] = useState<TFileUploadEntry[]>([]);
        const [batchComplete, setBatchComplete] = useState(false);

        const financialFilesState = useFinancialFiles(selectedDealId || null);

        useEffect(() => {
            onMount();
            return onUnmount;
        }, [onMount, onUnmount]);

        const handleDealChange = (dealId: string) => {
            setSelectedDealId(dealId);
            setFinancialFileId("");
        };

        const handleReset = () => {
            setSelectedDealId("");
            setPeriod(getCurrentPeriod());
            setFinancialFileId("");
            setNotes("");
            setSelectedFiles([]);
            setBatchEntries([]);
            setBatchComplete(false);
        };

        const uploadWithConcurrency = useCallback(
            async (entries: TFileUploadEntry[]) => {
                const queue = [...entries];
                const results: TFileUploadEntry[] = [...entries];

                const worker = async () => {
                    while (queue.length > 0) {
                        const entry = queue.shift();
                        if (!entry) break;
                        const idx = results.indexOf(entry);

                        results[idx] = { ...entry, status: "uploading" };
                        setBatchEntries([...results]);

                        try {
                            await onUpload({
                                dealId: selectedDealId,
                                friendlyName: stripExtension(entry.file.name),
                                period,
                                financialFileId: financialFileId || null,
                                notes: notes.trim() || null,
                                file: entry.file,
                            });
                            results[idx] = { ...entry, status: "success" };
                        } catch (err: unknown) {
                            const message = err instanceof Error ? err.message : "Upload failed";
                            results[idx] = { ...entry, status: "error", error: message };
                        }
                        setBatchEntries([...results]);
                    }
                };

                const workers = Array.from(
                    { length: Math.min(CONCURRENCY_LIMIT, entries.length) },
                    () => worker(),
                );
                await Promise.all(workers);
                return results;
            },
            [selectedDealId, period, financialFileId, notes, onUpload],
        );

        const handleSubmit = async (e: React.FormEvent) => {
            e.preventDefault();
            if (selectedFiles.length === 0 || !selectedDealId) return;

            setIsSubmitting(true);
            const entries: TFileUploadEntry[] = selectedFiles.map((file) => ({
                file,
                status: "pending" as const,
            }));
            setBatchEntries(entries);

            await uploadWithConcurrency(entries);

            setIsSubmitting(false);
            setBatchComplete(true);
        };

        const handleRetryFailed = async () => {
            if (!batchEntries.some((e) => e.status === "error")) return;

            setIsSubmitting(true);
            setBatchComplete(false);

            // Merge: keep successes, replace failures with pending
            const updatedEntries = batchEntries.map((e) =>
                e.status === "error" ? { ...e, status: "pending" as const, error: undefined } : e,
            );
            setBatchEntries(updatedEntries);

            const retryResults = await uploadWithConcurrency(
                updatedEntries.filter((e) => e.status === "pending"),
            );

            // Merge retry results back
            const finalEntries = updatedEntries.map((e) => {
                if (e.status === "pending") {
                    const result = retryResults.find((r) => r.file === e.file);
                    return result ?? e;
                }
                return e;
            });
            setBatchEntries(finalEntries);
            setIsSubmitting(false);
            setBatchComplete(true);
        };

        if (!canUpload) {
            return (
                <PageLayout
                    title="Upload Supporting Files"
                    description="Upload supporting documentation and files"
                >
                    <Card className="max-w-2xl">
                        <CardContent className="pt-6">
                            <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
                                <div className="mb-4 rounded-full bg-muted p-6">
                                    <ShieldX className="h-12 w-12 text-muted-foreground" />
                                </div>
                                <h3 className="text-xl font-semibold mb-2">Upload Not Available</h3>
                                <p className="text-sm text-muted-foreground max-w-md">
                                    Supporting file upload is only available for Business Partners
                                    with upload permissions enabled.
                                </p>
                            </div>
                        </CardContent>
                    </Card>
                </PageLayout>
            );
        }

        // Batch upload complete screen
        if (batchComplete) {
            const successCount = batchEntries.filter((e) => e.status === "success").length;
            const failedCount = batchEntries.filter((e) => e.status === "error").length;
            const allSucceeded = failedCount === 0;

            return (
                <PageLayout
                    title="Upload Supporting Files"
                    description="Upload supporting documentation and files"
                >
                    <Card className="max-w-2xl">
                        <CardContent className="pt-6">
                            <div className="flex flex-col items-center justify-center py-8 px-4 text-center">
                                <div
                                    className={`mb-4 rounded-full p-6 ${allSucceeded ? "bg-green-100" : "bg-yellow-100"}`}
                                >
                                    {allSucceeded ? (
                                        <CheckCircle2 className="h-12 w-12 text-green-600" />
                                    ) : (
                                        <AlertCircle className="h-12 w-12 text-yellow-600" />
                                    )}
                                </div>
                                <h3 className="text-xl font-semibold mb-2">
                                    {allSucceeded
                                        ? "Upload Successful"
                                        : "Upload Partially Complete"}
                                </h3>
                                <p className="text-sm text-muted-foreground max-w-md mb-4">
                                    {successCount} of {batchEntries.length} file
                                    {batchEntries.length !== 1 ? "s" : ""} uploaded successfully.
                                    {failedCount > 0 &&
                                        ` ${failedCount} file${failedCount !== 1 ? "s" : ""} failed.`}
                                </p>

                                {failedCount > 0 && (
                                    <div className="w-full max-w-md mb-4 space-y-2">
                                        {batchEntries
                                            .filter((e) => e.status === "error")
                                            .map((entry, i) => (
                                                <div
                                                    key={i}
                                                    className="rounded-lg bg-destructive/10 p-3 text-left text-sm"
                                                >
                                                    <p className="font-medium text-destructive">
                                                        {entry.file.name}
                                                    </p>
                                                    <p className="text-destructive/80">
                                                        {entry.error}
                                                    </p>
                                                </div>
                                            ))}
                                    </div>
                                )}

                                <div className="flex gap-3">
                                    {failedCount > 0 && (
                                        <Button
                                            variant="outline"
                                            onClick={handleRetryFailed}
                                            disabled={isSubmitting}
                                        >
                                            <RefreshCw className="mr-2 h-4 w-4" />
                                            Retry Failed
                                        </Button>
                                    )}
                                    <Button onClick={handleReset}>Upload More</Button>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </PageLayout>
            );
        }

        // Batch upload in progress screen
        if (isSubmitting && batchEntries.length > 0) {
            const completedCount = batchEntries.filter(
                (e) => e.status === "success" || e.status === "error",
            ).length;

            return (
                <PageLayout
                    title="Upload Supporting Files"
                    description="Upload supporting documentation and files"
                >
                    <Card className="max-w-2xl">
                        <CardContent className="pt-6">
                            <div className="py-8 px-4">
                                <div className="flex items-center justify-center gap-3 mb-6">
                                    <Loader size="lg" />
                                    <p className="text-lg font-medium">
                                        Uploading {completedCount} of {batchEntries.length} files...
                                    </p>
                                </div>
                                <div className="space-y-2">
                                    {batchEntries.map((entry, i) => (
                                        <div
                                            key={i}
                                            className="flex items-center gap-3 rounded-lg border px-3 py-2"
                                        >
                                            {entry.status === "pending" && (
                                                <div className="h-4 w-4 rounded-full border-2 border-muted-foreground" />
                                            )}
                                            {entry.status === "uploading" && <Loader size="sm" />}
                                            {entry.status === "success" && (
                                                <CheckCircle2 className="h-4 w-4 text-green-600" />
                                            )}
                                            {entry.status === "error" && (
                                                <AlertCircle className="h-4 w-4 text-destructive" />
                                            )}
                                            <span className="text-sm truncate flex-1">
                                                {entry.file.name}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </PageLayout>
            );
        }

        const isFormValid = Boolean(selectedDealId && selectedFiles.length > 0);

        return (
            <PageLayout
                title="Upload Supporting Files"
                description="Upload supporting documentation and files"
            >
                <Card className="max-w-2xl">
                    <CardContent className="pt-6">
                        <form onSubmit={handleSubmit} className="space-y-4">
                            <FormField label="Deal" htmlFor="deal" required>
                                <select
                                    id="deal"
                                    value={selectedDealId}
                                    onChange={(e) => handleDealChange(e.target.value)}
                                    className="w-full px-3 py-2 border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary"
                                    required
                                    disabled={isSubmitting}
                                >
                                    <option value="">
                                        {dealsState.loading
                                            ? "Loading deals..."
                                            : "Select a deal..."}
                                    </option>
                                    {dealsState.data?.map((deal) => (
                                        <option key={deal.id} value={deal.id}>
                                            {deal.name}
                                        </option>
                                    ))}
                                </select>
                            </FormField>

                            <FormField label="Reporting Period" htmlFor="period" required>
                                <MonthYearPicker
                                    id="period"
                                    value={period}
                                    onChange={setPeriod}
                                    required
                                    inline
                                    className="w-1/2 max-w-full min-w-0"
                                />
                            </FormField>

                            <FormField
                                label="Related File"
                                htmlFor="financialFile"
                                description="Optionally link to a specific financial file"
                            >
                                <select
                                    id="financialFile"
                                    value={financialFileId}
                                    onChange={(e) => setFinancialFileId(e.target.value)}
                                    className="w-full px-3 py-2 border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary"
                                    disabled={!selectedDealId || isSubmitting}
                                >
                                    <option value="">
                                        {!selectedDealId
                                            ? "Select a deal first..."
                                            : financialFilesState.loading
                                              ? "Loading files..."
                                              : "None (optional)"}
                                    </option>
                                    {financialFilesState.data?.map((file) => (
                                        <option key={file.id} value={file.id}>
                                            {file.name}
                                        </option>
                                    ))}
                                </select>
                            </FormField>

                            <FormField
                                label="Notes"
                                htmlFor="notes"
                                description={`${notes.length}/500 characters`}
                            >
                                <textarea
                                    id="notes"
                                    value={notes}
                                    onChange={(e) => setNotes(e.target.value)}
                                    className="w-full px-3 py-2 border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary resize-none"
                                    placeholder="Optional notes about this file..."
                                    maxLength={500}
                                    rows={3}
                                    disabled={isSubmitting}
                                />
                            </FormField>

                            <FormField
                                label={`Files (up to ${MAX_FILES})`}
                                htmlFor="supportingFile"
                                required
                            >
                                <FileUploadZone
                                    id="supportingFile"
                                    multiple
                                    maxFiles={MAX_FILES}
                                    onFilesSelect={setSelectedFiles}
                                    accept={supportingFileAccept}
                                    maxSize={50 * 1024 * 1024}
                                    disabled={isSubmitting}
                                    className="min-h-[120px]"
                                />
                            </FormField>

                            <div className="flex justify-end pt-2">
                                <Button type="submit" disabled={!isFormValid || isSubmitting}>
                                    {isSubmitting ? (
                                        <>
                                            <Loader size="sm" className="mr-2" />
                                            Uploading...
                                        </>
                                    ) : (
                                        <>
                                            <Upload className="mr-2 h-4 w-4" />
                                            Upload {selectedFiles.length || ""} File
                                            {selectedFiles.length !== 1 ? "s" : ""}
                                        </>
                                    )}
                                </Button>
                            </div>
                        </form>
                    </CardContent>
                </Card>
            </PageLayout>
        );
    };

    return ViewUploadSupporting;
};
